import React, { useEffect, useMemo, useState } from "react";
import { AdminCandidateListRow } from "../admin-types";
import { adminService } from "../admin.service";
import {
  Column,
  Empty,
  Panel,
  Table,
  SortConfig,
  SortDirection,
  sortData,
} from "../AdminUI";

interface Props {
  onSelectCandidate: (c: AdminCandidateListRow) => void;
  cached: AdminCandidateListRow[] | null;
  onCache: (data: AdminCandidateListRow[]) => void;
  refreshKey: number;
}

const normalizeRows = (rows: AdminCandidateListRow[]) =>
  rows.map((row) => ({
    ...row,
    user_id: row.user_id || row.user,
    candidate_id: row.candidate_id || row.id,
  }));

export default function CandidatesList({
  onSelectCandidate,
  cached,
  onCache,
  refreshKey,
}: Props) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [candidates, setCandidates] = useState<AdminCandidateListRow[]>(
    cached || []
  );
  const [search, setSearch] = useState("");
  const [sortConfig, setSortConfig] = useState<SortConfig | null>(null);

  const handleSort = (key: string) => {
    let direction: SortDirection = "asc";
    if (
      sortConfig &&
      sortConfig.key === key &&
      sortConfig.direction === "asc"
    ) {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return candidates;
    return candidates.filter(
      (c) =>
        (c.first_name || "").toLowerCase().includes(q) ||
        (c.last_name || "").toLowerCase().includes(q) ||
        (c.email || "").toLowerCase().includes(q) ||
        (c.city || "").toLowerCase().includes(q) ||
        (c.country || "").toLowerCase().includes(q) ||
        (c.nif || "").toLowerCase().includes(q)
    );
  }, [candidates, search]);

  const sortedRows = useMemo(() => {
    return sortData(filtered, sortConfig);
  }, [filtered, sortConfig]);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        if (cached && refreshKey === 0) {
          const normalized = normalizeRows(cached);
          setCandidates(normalized);
          setLoading(false);
          return;
        }
        setLoading(true);
        const data = await adminService.fetchCandidates();
        if (!mounted) return;
        const normalized = normalizeRows(data || []);
        if (normalized.length > 0) {
          const sample = normalized[0];
          console.log("[Admin] candidates list sample", {
            id: sample.id,
            user_id: sample.user_id,
            candidate_id: sample.candidate_id,
            user: (sample as any).user,
          });
        }
        setCandidates(normalized);
        onCache(normalized);
      } catch (e: any) {
        if (mounted) setError(e.message);
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    return () => {
      mounted = false;
    };
  }, [refreshKey]);

  const cols: Column<AdminCandidateListRow>[] = [
    {
      key: "first_name",
      header: "Nombre",
      sortable: true,
      render: (r) => <span className="font-medium">{r.first_name || "—"}</span>,
    },
    { key: "last_name", header: "Apellido", sortable: true },
    { key: "email", header: "Email", sortable: true },
    { key: "phone", header: "Teléfono", sortable: true },
    { key: "nif", header: "NIF", sortable: true },
    { key: "city", header: "Ciudad", sortable: true },
    { key: "country", header: "País", sortable: true },
    {
      key: "created_at",
      header: "Creado",
      sortable: true,
      render: (r) =>
        r.created_at ? new Date(r.created_at).toLocaleDateString() : "—",
    },
  ];

  return (
    <Panel
      title="Candidatos"
      right={
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Buscar candidatos..."
          className="input"
        />
      }
    >
      {loading ? (
        <Empty>Cargando candidatos…</Empty>
      ) : error ? (
        <Empty>Error: {error}</Empty>
      ) : sortedRows.length ? (
        <Table
          columns={cols}
          rows={sortedRows}
          keyField="id"
          onRowClick={onSelectCandidate}
          onSort={handleSort}
          sortConfig={sortConfig}
        />
      ) : (
        <Empty>No se encontraron candidatos.</Empty>
      )}
    </Panel>
  );
}
